import { gql, useQuery } from "@apollo/client";
import { IconButton, Skeleton } from "@mui/material";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useMemo } from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";
import {
  coordinateToMapPoint,
  MapMarkerInput
} from "@caredna-marlin/map-wrapper";
import { default as GenericMapWrapper } from "@caredna-marlin/map-wrapper/google-map-wrapper";
import { CensusPatientLocationQuery } from "~/graphql/__generated__/graphql";
import PatientLocationSearchBox, { mapHeight, mapWidth } from "../route/findPatientLocation";
import { useMapPatientInfo } from "~/state/map/mapPatientInfo";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

const CENSUS_LIMIT = 100;

export default function CensusLocationIndexView() {
  const navigate = useNavigate();
  const orgConfig = useOrgConfig();
  const {mapPatientInfo, loadingPatientInfo, setMapPatientId} = useMapPatientInfo();

  const { data, loading } = useQuery<CensusPatientLocationQuery>(CENSUS_LOCATIONS_QUERY, {
    variables: {
      limit: CENSUS_LIMIT + 1
    }}
  );

  const markerInputs: MapMarkerInput[] = useMemo(() => {
    if (!data?.activePatients) return [];

    const markerInputList: MapMarkerInput[] = [];

    data?.activePatients.slice(0,CENSUS_LIMIT).forEach((patient) => {
      const coordinate = patient.addressCoordinates
      const point = coordinate ? coordinateToMapPoint(coordinate) : null
      if (point) {
        markerInputList.push({
          label: `${patient.firstName} ${patient.lastName}`,
          timestamp: new Date(),
          highlighted: true,
          icon: "dot",
          point: point,
          patient: patient,
          colorIndex: 1, // Not red since the patient marker is red
        });
      }
    });

    return markerInputList;
  }, [data]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton size="small" onClick={() => { navigate(-1) }} color="primary">
          <ArrowBackIosIcon />
        </IconButton>
        <h1>Active Patient Locations</h1>
      </div>
      <PatientLocationSearchBox mapPatientInfo={mapPatientInfo} setMapPatientId={setMapPatientId} loadingPatientInfo={loadingPatientInfo}/>
      {loading && !data ? (
        <Skeleton variant="rectangular" width={mapWidth} height={mapHeight} />
      ) : (
        <GenericMapWrapper
          mapsApiKey={orgConfig.GOOGLE_MAPS_WEB_API_KEY}
          displayRoute={false}
          clusterMarkers={false}
          height={mapHeight}
          width={mapWidth}
          markerInputs={markerInputs}
          trackingPoints={[]}
          targetLocation={mapPatientInfo?.location}
          onPatientClick={(patientId: number) => {
            navigate(`/patients/${patientId}`);
          }}
        />
      )}
      {(data?.activePatients?.length || 0) > CENSUS_LIMIT && (
        <div style={{ color: 'orange', marginBottom: '5px' }}>
          <WarningAmberRoundedIcon color="warning" sx={{ verticalAlign: "bottom" }} />
          Not all active patients are shown. Currently limited to {CENSUS_LIMIT} patients.
        </div>
      )}
    </>
  );
}

// TODO: change permission
const CENSUS_LOCATIONS_QUERY = gql`
  query CensusPatientLocation($limit: Int!)
  @permission(requires: useWebApp)
  {
    activePatients(offset: 0, orderBy: {id: {direction: asc, priority: 1}} limit: $limit) {
      id
      firstName
      lastName
      addressCoordinates
    }
  }
`;
